import { useState } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

import useAccountQuery from 'api/queries/useAccountQuery';
import useEBTChargeAmountQuery from 'api/queries/useEBTChargeAmountQuery';
import useSubscriptionQuery from 'api/queries/useSubscriptionQuery';
import { Alert } from 'honeydew/Alert';
import Button from 'honeydew/Button';
import Card from 'honeydew/Card';
import useGlobalStore from 'hooks/useGlobalStore';
import DrawerModal from 'shared/DrawerModal';
import { EBTCardLast4 } from 'shared/ebt/EBTSharedUI';
import { formatDateString, localizeTimeZone } from 'utils/dateUtils';
import { getCartSnapEligibleSubtotals } from 'utils/priceUtils';

function EBTCartHeader({ isOrderMinMet, selections }) {
    const { data: accountData, isLoading: isAccountLoading } = useAccountQuery();
    const { data: subscriptionData, isLoading: isSubscriptionLoading } = useSubscriptionQuery();
    const { data: ebtChargeAmountData, isLoading: isEBTChargeAmountLoading } = useEBTChargeAmountQuery();

    const { setEbtPaymentFlowOpen } = useGlobalStore();

    const [isPaymentInfoDrawerOpen, setIsPaymentInfoDrawerOpen] = useState(false);

    const { snapEligibleSubtotal } = getCartSnapEligibleSubtotals({ selections });

    const isLoading = isAccountLoading || isSubscriptionLoading || isEBTChargeAmountLoading;
    const hasEBTCard = subscriptionData?.ebtCard?.last4;

    if (isLoading || !hasEBTCard) return null;

    const ebtAmount = ebtChargeAmountData?.ebtAmount;
    const formattedEbtAmount = ebtAmount?.toFixed(2) ?? '0.00';

    return (
        <div className="my-10">
            {ebtAmount > 0 ? (
                <>
                    <Card bgColor="bg-off-white" marginClasses="my-10" flush>
                        <div className="flex flex-col gap-10 p-15">
                            <div className="flex items-center justify-between">
                                <h4 className="text-body-md font-grotesk-bold">SNAP EBT Payment</h4>
                                <button
                                    type="button"
                                    onClick={() => setEbtPaymentFlowOpen(true)}
                                    className="link text-body-sm"
                                >
                                    Edit
                                </button>
                            </div>
                            <div className="flex items-center gap-10">
                                <div className="shrink-0 self-start">
                                    <img
                                        src="https://img-cdn.misfitsmarket.com/boysenberry/logos/snap-logo.svg"
                                        alt="SNAP"
                                        width={30}
                                        height={30}
                                    />
                                </div>
                                <div>
                                    <EBTCardLast4 last4={subscriptionData?.ebtCard?.last4} />
                                    <p>
                                        <span className="text-body-sm">
                                            Payment:{' '}
                                            <span className="text-brand-primary font-grotesk-bold">
                                                ${formattedEbtAmount}
                                            </span>{' '}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => setIsPaymentInfoDrawerOpen(true)}
                                            aria-label="Payment information"
                                            className="relative top-[3px]"
                                        >
                                            <InformationCircleIcon width={18} />
                                        </button>
                                        {isPaymentInfoDrawerOpen && (
                                            <DrawerModal
                                                open={isPaymentInfoDrawerOpen}
                                                onClose={() => setIsPaymentInfoDrawerOpen(false)}
                                            >
                                                <div className="space-y-20">
                                                    <h3 className="text-heading-xs">EBT Payment Details</h3>
                                                    <p>
                                                        You can use your EBT card to pay for the total amount of
                                                        SNAP-eligible items in your cart (or only some items, if
                                                        you&apos;d like!). Before your shopping window closes, you will
                                                        need to apply EBT payment by entering your pin and confirming
                                                        the amount you want applied to the order.
                                                    </p>
                                                    <p>
                                                        Any remaining order balance (including SNAP-eligible items not
                                                        paid for with EBT, as well as all fees, delivery charges, and
                                                        non-SNAP-eligible items) will be charged to your default payment
                                                        method once your shopping window closes.
                                                    </p>
                                                    <p>
                                                        If you add additional items to your cart before your shopping
                                                        window closes and your SNAP-eligible total increases, or if you
                                                        want to increase the amount of your EBT payment up to the
                                                        SNAP-eligible total, you can update the amount paid with your
                                                        EBT card at any time prior to the close of your shopping window
                                                        by clicking the &quot;Edit&quot; button in your cart.{' '}
                                                    </p>
                                                    <p>
                                                        If you remove items from your cart and your SNAP-eligible total
                                                        falls below the amount you&apos;ve paid with your EBT card, any
                                                        extra EBT payment will automatically be refunded back to your
                                                        EBT card when your window closes.
                                                    </p>
                                                </div>
                                            </DrawerModal>
                                        )}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </Card>
                    <Alert variant="info-circle">
                        <p className="text-body-xs">
                            The remainder of your order will be charged to your default payment method on{' '}
                            {formatDateString(accountData?.nextOrder?.orderChargeDate?.date, true, false, false, true)}.
                        </p>
                    </Alert>
                </>
            ) : (
                <div className="flex flex-col gap-10">
                    {subscriptionData?.ebtCard?.last4 && snapEligibleSubtotal > 0 && (
                        <Button onClick={() => setEbtPaymentFlowOpen(true)}>Make a payment with SNAP EBT</Button>
                    )}
                    {isOrderMinMet && (
                        <Alert variant="info-circle">
                            <p className="text-body-xs">
                                If you don&apos;t make a payment with your EBT card before your shopping window closes
                                on {formatDateString(accountData?.marketCloseDate?.date, true, false, false, true)} at{' '}
                                {localizeTimeZone(accountData?.marketCloseDate?.date)}, your cart total will be charged
                                to your default payment method.{' '}
                                {subscriptionData?.variant === 'autoship' ? (
                                    <>
                                        If you don&apos;t want to receive an order this week, you can{' '}
                                        <Link
                                            to="/account/upcoming-deliveries"
                                            className="underline"
                                            onClick={(e) => {
                                                if (window.isNativeApp) {
                                                    e.preventDefault();
                                                    window.ReactNativeWebView?.postMessage(
                                                        JSON.stringify({ navigate: 'Upcoming' })
                                                    );
                                                }
                                            }}
                                        >
                                            skip your order
                                        </Link>{' '}
                                        or clear your cart.
                                    </>
                                ) : (
                                    <>If you don&apos;t want to receive an order this week, you can clear your cart.</>
                                )}
                            </p>
                        </Alert>
                    )}
                </div>
            )}
        </div>
    );
}

export default EBTCartHeader;
